'use client';

import React, { useState, useEffect, useRef } from 'react';

const slogans = [
  {
    audience: 'CONSUMIDOR',
    audienceLabel: 'Para ti',
    tag: '¡Para consumidores!',
    line1: 'BÚSCA,',
    line2: 'CONECTA',
    line3: 'Y COMPRA!',
    sub: 'Encuentra productos y servicios locales cerca de ti en tiempo real. Sin esfuerzo, sin rodeos.',
    cta: '¡Quiero buscar!',
    ctaSecondary: 'Ver cómo funciona',
    color: '#F48D31',
    bgAccent: 'rgba(244,141,49,0.12)',
  },
  {
    audience: 'ALIADO',
    audienceLabel: 'Para tu negocio',
    tag: '¡Para partners!',
    line1: 'ÚNETE,',
    line2: 'CONECTA',
    line3: 'Y VENDE!',
    sub: 'Registra tu negocio, conecta con miles de clientes cercanos y aumenta tus ventas desde hoy.',
    cta: '¡Quiero vender!',
    ctaSecondary: 'Ver beneficios',
    color: '#00C9B8',
    bgAccent: 'rgba(0,201,184,0.1)',
  },
];

// Map pin positions for the visual
const mapPins = [
  { x: '20%', y: '30%', color: '#F48D31', size: 'lg', delay: 0 },
  { x: '65%', y: '20%', color: '#00C9B8', size: 'md', delay: 0.5 },
  { x: '75%', y: '55%', color: '#F7A85A', size: 'lg', delay: 1 },
  { x: '40%', y: '65%', color: '#02D7D7', size: 'sm', delay: 1.5 },
  { x: '55%', y: '40%', color: '#00C9B8', size: 'md', delay: 0.8 },
  { x: '30%', y: '55%', color: '#F48D31', size: 'sm', delay: 1.2 },
];

function MapPin({ color, size, animClass }: { color: string; size: 'sm' | 'md' | 'lg'; animClass: string }) {
  const sizes = { sm: 20, md: 28, lg: 36 };
  const s = sizes[size];
  return (
    <svg
      width={s}
      height={s * 1.3}
      viewBox="0 0 28 36"
      fill="none"
      className={animClass}
      style={{ filter: `drop-shadow(0 4px 12px ${color}60)` }}
    >
      <path
        d="M14 0C6.268 0 0 6.268 0 14C0 24.5 14 36 14 36C14 36 28 24.5 28 14C28 6.268 21.732 0 14 0ZM14 20C10.686 20 8 17.314 8 14C8 10.686 10.686 8 14 8C17.314 8 20 10.686 20 14C20 17.314 17.314 20 14 20Z"
        fill={color}
      />
      <circle cx="14" cy="14" r="4" fill="white" opacity="0.9" />
    </svg>
  );
}

export default function HeroSection() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const switchSlogan = (idx: number) => {
    if (idx === activeIdx) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActiveIdx(idx);
      setIsAnimating(false);
    }, 300);
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % slogans.length);
    }, 4000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const current = slogans[activeIdx];

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden noise-overlay"
      style={{ paddingTop: '100px', paddingBottom: '60px' }}
    >
      {/* Background layers */}
      <div className="absolute inset-0 grid-pattern opacity-60" />
      <div className="absolute inset-0 hero-glow" />

      {/* Animated background orbs */}
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-[120px] opacity-20 transition-all duration-1000"
        style={{ background: current.color }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-72 h-72 rounded-full blur-[100px] opacity-15 transition-all duration-1000"
        style={{ background: activeIdx === 0 ? '#00C9B8' : '#F48D31' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: Text content */}
          <div className="space-y-8">
            {/* Audience toggle */}
            <div className="flex items-center gap-2">
              {slogans.map((s, i) => (
                <button
                  key={i}
                  onClick={() => {
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    switchSlogan(i);
                  }}
                  className={`px-5 py-2 rounded-full text-[11px] font-black uppercase tracking-widest transition-all duration-300 ${
                    i === activeIdx
                      ? 'text-bg shadow-lg scale-105'
                      : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/80'
                  }`}
                  style={
                    i === activeIdx
                      ? { background: current.color, boxShadow: `0 4px 20px ${current.color}50` }
                      : {}
                  }
                >
                  {s.audienceLabel}
                </button>
              ))}
              <div className="flex items-center gap-1.5 ml-3">
                <div
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: current.color }}
                />
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">
                  Próximamente
                </span>
              </div>
            </div>

            {/* Stacked slogans */}
            <div className="space-y-1">
              {slogans.map((s, i) => (
                <div
                  key={i}
                  onClick={() => {
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    switchSlogan(i);
                  }}
                  className={`hero-slogan cursor-pointer ${i === activeIdx ? 'active' : ''}`}
                >
                  <h1
                    className="text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.85] uppercase"
                    style={{
                      color: i === activeIdx ? '#FFFFFF' : 'rgba(255,255,255,0.15)',
                    }}
                  >
                    {s.line1}
                    <br />
                    <span style={{ color: i === activeIdx ? s.color : 'rgba(255,255,255,0.15)' }}>
                      {s.line2}
                    </span>
                    <br />
                    {s.line3}
                  </h1>
                </div>
              ))}
            </div>

            {/* Sub description */}
            <div
              className={`transition-all duration-300 ${isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}
            >
              <p className="text-lg text-muted leading-relaxed max-w-lg font-medium">
                {current.sub}
              </p>
            </div>

            {/* CTAs */}
            <div
              className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 transition-all duration-300 ${isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}
            >
              <a
                href="#registro"
                className="px-8 py-4 font-black text-sm uppercase tracking-widest rounded-2xl text-bg hover:scale-105 transition-all shadow-xl"
                style={{
                  background: current.color,
                  boxShadow: `0 8px 30px ${current.color}50`,
                }}
              >
                {current.cta}
              </a>
              <a
                href="#como-funciona"
                className="flex items-center gap-2 px-8 py-4 border border-white/15 rounded-2xl text-sm font-bold text-white/70 hover:text-white hover:border-white/30 transition-all"
              >
                {current.ctaSecondary}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>

            {/* Stats strip */}
            <div className="flex items-center gap-8 pt-4 border-t border-white/5">
              {[
                { label: 'Categorías', value: '8+' },
                { label: 'Partners registrados', value: '500+' },
                { label: 'Ciudades', value: 'Pronto' },
              ].map((stat) => (
                <div key={stat.label} className="space-y-1">
                  <p className="text-2xl font-black text-white">{stat.value}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Map visualization */}
          <div className="relative h-[480px] lg:h-[560px]">
            {/* Map background card */}
            <div
              className="absolute inset-0 rounded-[40px] border overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #13151F 0%, #1C1F2E 100%)',
                borderColor: 'rgba(255,255,255,0.08)',
              }}
            >
              {/* Grid lines on map */}
              <div className="absolute inset-0 grid-pattern opacity-40" />

              {/* Circular rings */}
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full border opacity-20"
                style={{ borderColor: current.color }}
              />
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full border opacity-10"
                style={{ borderColor: current.color }}
              />
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border opacity-5"
                style={{ borderColor: current.color }}
              />

              {/* Center glow */}
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full blur-[60px] opacity-30 transition-all duration-1000"
                style={{ background: current.color }}
              />

              {/* Map pins */}
              {mapPins.map((pin, i) => (
                <div
                  key={i}
                  className="absolute"
                  style={{
                    left: pin.x,
                    top: pin.y,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <MapPin
                    color={pin.color}
                    size={pin.size as 'sm' | 'md' | 'lg'}
                    animClass={i % 2 === 0 ? 'animate-pin-1' : 'animate-pin-2'}
                  />
                </div>
              ))}

              {/* Center main pin (larger, prominent) */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[60%] z-20">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-2xl animate-float"
                  style={{
                    background: current.color,
                    boxShadow: `0 10px 40px ${current.color}60`,
                  }}
                >
                  <svg width="32" height="32" viewBox="0 0 36 36" fill="none">
                    <path d="M18 4C12.477 4 8 8.477 8 14C8 21.75 18 32 18 32C18 32 28 21.75 28 14C28 8.477 23.523 4 18 4ZM18 19C15.239 19 13 16.761 13 14C13 11.239 15.239 9 18 9C20.761 9 23 11.239 23 14C23 16.761 20.761 19 18 19Z" fill="white" />
                    <circle cx="18" cy="14" r="3" fill={current.color} />
                  </svg>
                </div>
              </div>

              {/* Floating info cards */}
              <div
                className="absolute bottom-8 left-6 right-6 glass rounded-2xl p-5 border z-30"
                style={{ borderColor: 'rgba(255,255,255,0.08)' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-muted mb-1">
                      {activeIdx === 0 ? 'Resultados encontrados' : 'Negocios conectados'}
                    </p>
                    <p className="text-2xl font-black text-white">
                      {activeIdx === 0 ? '1,247 productos' : '384 partners activos'}
                    </p>
                  </div>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `${current.color}20`, border: `1px solid ${current.color}30` }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={current.color} strokeWidth="2">
                      {activeIdx === 0 ? (
                        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" />
                      ) : (
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" strokeLinejoin="round" />
                      )}
                    </svg>
                  </div>
                </div>
                {/* Mini bar */}
                <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{
                      width: activeIdx === 0 ? '78%' : '62%',
                      background: current.color,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-[10px] font-bold uppercase tracking-widest text-white">Descubrir</span>
        <div className="w-px h-8 bg-gradient-to-b from-white to-transparent" />
      </div>
    </section>
  );
}